import { isNil } from 'lodash';
import moment from 'moment';
import {
  isDateComponent,
  isNumberComponent,
  isPersonComponent,
  isRelatedCardComponent,
  isTagComponent,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { uuid as getUuid } from '@shared/util/uuid';
import {
  ControlsState,
  FilterOption,
  FilterSelection,
  FilterSelectionState,
  NumberOperator,
  SortOption,
  SortSelectionState,
} from '../types';

export function mapCollectionCriteriaToState(
  collectionCriteria: NonNullable<API.ViewTemplateFragment['collectionCriteria']> | null,
  availableFilters: FilterOption[],
  availableSorts: SortOption[],
  availableGroups: FilterOption[],
): ControlsState {
  // TODO
  // omniSearchQuery if saved

  const filters = mapFilterByComponentToState(
    collectionCriteria?.filterByComponents ?? [],
    availableFilters,
  );

  const sorts = mapSortByComponentToState(
    collectionCriteria?.sortByComponents ?? [],
    availableSorts,
  );

  const groups = mapGroupsToState(collectionCriteria?.groups ?? [], availableGroups);

  return {
    filters,
    availableFilters,
    selectedFilters: Array.from(filters.values())
      .map(filter => filter.filter)
      .filter(notNil),

    sorts,
    availableSorts,
    selectedSorts: Array.from(sorts.values())
      .map(sort => sort.sort)
      .filter(notNil),

    groups,
    availableGroups,
    selectedGroups: groups.map(g =>
      Array.from(g.values())
        .map(group => group.filter)
        .filter(notNil),
    ),

    search: null,
  };
}

export function mapFilterByComponentToState(
  filterByComponents: API.FilterByComponentFragment[],
  availableFilters: FilterOption[],
): Map<string, FilterSelectionState> {
  const filters = new Map<string, FilterSelectionState>();
  filterByComponents?.forEach(filter => {
    const option = availableFilters.find(
      filterOption => filterOption.component?.id === filter.componentId,
    );
    if (!isNil(option) && !isNil(option.component)) {
      const uuid = option.uuid ?? getUuid();
      let selection: FilterSelection | null = null;
      let negate = false;
      if (isTagComponent(option.component)) {
        if (!isNil(filter.value.containsString?.values)) {
          selection = filter.value.containsString.values.map(value => ({
            type: 'TAG',
            filter: { uuid, ...option },
            value,
          }));
          negate = !!filter.value.containsString.negate;
        } else {
          selection = [{ type: 'TAG', filter: { uuid, ...option }, value: 'empty' }];
        }
      }
      if (isRelatedCardComponent(option.component)) {
        if (!isNil(filter.value.containsString?.values)) {
          selection = filter.value.containsString.values.map(value => ({
            type: 'RELATED_CARD',
            filter: { uuid, ...option },
            value,
          }));
        } else {
          selection = [{ type: 'RELATED_CARD', filter: { uuid, ...option }, value: 'empty' }];
        }
      }
      if (isPersonComponent(option.component)) {
        const personFilter = { uuid, ...option };
        if (!isNil(filter.value.containsNumber?.values)) {
          selection = filter.value.containsNumber.values.map(value => ({
            type: 'PERSON',
            filter: personFilter,
            value,
          }));
        } else if (!isNil(filter.value.containsCurrentUser)) {
          selection = [{ type: 'PERSON', filter: personFilter, value: 'currentUser' }];
        } else {
          selection = [{ type: 'PERSON', filter: personFilter, value: 'empty' }];
        }
      }
      if (isTextComponent(option.component)) {
        selection = filter.value.isEmpty
          ? { type: 'TEXT', operator: 'empty' }
          : { type: 'TEXT', operator: 'contains', value: filter.value.stringWildcard?.value };
      }
      if (isDateComponent(option.component)) {
        if (filter.value.isEmpty) {
          selection = { type: 'DATE', operator: 'empty' };
        } else if (!isNil(filter.value.dateIs)) {
          // TODO making assumptions about 0 here since API data
          // doesn't indicate if 'before' or 'after' today
          // were selected in the UI. Behavior
          // is equivalent with 0 duration, but that might change
          // if we change today to be start of day.
          const relativeDate = filter.value.dateIs.relativeDate!;
          if (
            relativeDate.duration < 0 ||
            (relativeDate.duration === 0 && ['GT', 'GTE'].includes(filter.value.dateIs.operator))
          ) {
            selection = {
              type: 'DATE',
              operator: 'daysBeforeToday',
              comparisonOperator: filter.value.dateIs.operator,
              duration: relativeDate.duration,
            };
          } else {
            selection = {
              type: 'DATE',
              operator: 'daysAfterToday',
              comparisonOperator: filter.value.dateIs.operator,
              duration: relativeDate.duration,
            };
          }
        } else if (!isNil(filter.value.dateTokenIs)) {
          selection = { type: 'DATE', operator: 'today' };
        } else if (
          !isNil(filter.value.dateRange?.after) &&
          !isNil(filter.value.dateRange?.before) &&
          moment(filter.value.dateRange?.after)
            .startOf('day')
            .toISOString() ===
            moment(filter.value.dateRange?.before)
              .startOf('day')
              .toISOString()
        ) {
          selection = {
            type: 'DATE',
            operator: 'exactly',
            after: filter.value.dateRange?.after ?? undefined,
            before: filter.value.dateRange?.before ?? undefined,
          };
        } else if (!isNil(filter.value.dateRange?.after) && isNil(filter.value.dateRange?.before)) {
          selection = {
            type: 'DATE',
            operator: 'startsOn',
            after: filter.value.dateRange?.after ?? undefined,
          };
        } else if (isNil(filter.value.dateRange?.after) && !isNil(filter.value.dateRange?.before)) {
          selection = {
            type: 'DATE',
            operator: 'endsOn',
            before: filter.value.dateRange?.before ?? undefined,
          };
        } else {
          selection = {
            type: 'DATE',
            operator: 'between',
            after: filter.value.dateRange?.after ?? undefined,
            before: filter.value.dateRange?.before ?? undefined,
          };
        }
      }
      if (isNumberComponent(option.component)) {
        if (filter.value.isEmpty?.value) {
          selection = { type: 'NUMBER', operator: 'empty', value: 0 };
        } else if (filter.value.numberIs) {
          selection = {
            type: 'NUMBER',
            operator: filter.value.numberIs.operator as NumberOperator,
            value: filter.value.numberIs.value,
          };
        } else {
          selection = null;
        }
      }

      if (selection) {
        filters.set(uuid, {
          filter: { uuid, ...option },
          selection,
          negate,
        });
      }
    }
  });

  return filters;
}

function mapSortByComponentToState(
  sortByComponents: API.SortByComponent[],
  availableSorts: SortOption[],
): Map<string, SortSelectionState> {
  const sorts = new Map<string, SortSelectionState>();
  sortByComponents?.forEach(sort => {
    const option = availableSorts.find(sortOption => sortOption.component?.id === sort.componentId);
    if (!isNil(option)) {
      const uuid = getUuid();
      const direction: API.SortDirection = sort.direction === 'ASC' ? 'ASC' : 'DESC';
      sorts.set(uuid, {
        sort: { uuid, ...option, direction },
        direction,
      });
    }
  });
  return sorts;
}

function mapGroupsToState(groups: API.CollectionGroup[], availableGroups: FilterOption[]) {
  return groups.map(group => {
    const groupState = mapFilterByComponentToState(group.filterByComponents ?? [], availableGroups);
    const groupMap = new Map<string, FilterSelectionState>();
    groupState.forEach(state => {
      if (!isNil(state.filter?.uuid)) {
        groupMap.set(state.filter?.uuid, state);
      }
    });
    return groupMap;
  });
}
